import React, { useState, useEffect } from "react";
import { Instagram, Phone, MapPin, MessageCircle, ChevronDown, Menu, X, Star } from "lucide-react";
import { clinicInfo, treatments } from "../mock/data";
import { Link } from "react-router-dom";
import { trackLead } from "../lib/analytics";

const NavItem = ({ href, children }) => (
  <a href={href} className="whitespace-nowrap text-[12px] 2xl:text-[13px] tracking-wide uppercase font-semibold text-[#0e2b2b] hover:text-[#14a68e] transition-colors">
    {children}
  </a>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tOpen, setTOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      {/* Top thin bar */}
      <div className="hidden md:flex bg-[#0e2b2b] text-white/90 text-[12px]">
        <div className="max-w-[1400px] mx-auto w-full px-8 py-2 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href={clinicInfo.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#5eead4] transition"><Instagram size={13} />{clinicInfo.instagramHandle}</a>
            <span className="opacity-30">|</span>
            <a href={`tel:${clinicInfo.phone}`} onClick={() => trackLead("phone_click", { button_location: "header_top" })} className="flex items-center gap-2 hover:text-[#5eead4] transition"><Phone size={13} />{clinicInfo.phoneDisplay}</a>
          </div>
          <div className="flex items-center gap-5">
            <a href="#contact" className="flex items-center gap-2 hover:text-[#5eead4] transition"><MapPin size={13} />{clinicInfo.location.split(",")[0]}</a>
            <span className="opacity-30">|</span>
            <a href={clinicInfo.whatsapp} target="_blank" rel="noreferrer" onClick={() => trackLead("whatsapp_click", { button_location: "header_top" })} className="flex items-center gap-2 hover:text-[#5eead4] transition"><MessageCircle size={13} />WhatsApp</a>
            <span className="opacity-30">|</span>
            <a href={clinicInfo.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#5eead4] transition"><Instagram size={13} />Instagram</a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-[#f6f7f4]/95 backdrop-blur-md border-b border-black/5 transition-shadow ${scrolled ? "shadow-sm" : ""}`}>
        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-8 xl:px-5 2xl:px-8 py-3 flex items-center justify-between gap-4 xl:gap-2 2xl:gap-4">
          {/* Logo */}
          <a href="#top" className="flex items-center gap-3 xl:gap-2 2xl:gap-3 shrink-0">
            <span className="w-10 h-10 rounded-full overflow-hidden shrink-0 flex items-center justify-center">
                <img
                  src="/images/branding/khurana-logo.png"
                  alt="Dr. Khurana's Dental & Aesthetic Clinic logo"
                  className="w-full h-full object-contain"
                />
              </span>
            <div className="leading-tight whitespace-nowrap">
              <div className="font-serif-display text-[20px] md:text-[22px] tracking-wider text-[#0e2b2b]">DR. KHURANA’S</div>
              <div className="text-[10px] tracking-[0.22em] text-[#14a68e] font-semibold">DENTAL &amp; AESTHETIC CLINIC</div>
            </div>
          </a>

          {/* Center nav */}
          <nav className="hidden xl:flex items-center gap-3 2xl:gap-6">
            <div className="relative" onMouseEnter={() => setTOpen(true)} onMouseLeave={() => setTOpen(false)}>
              <button className="whitespace-nowrap flex items-center gap-1 text-[13px] tracking-wide uppercase font-semibold text-[#0e2b2b] hover:text-[#14a68e] transition-colors">
                Treatments <ChevronDown size={14} />
              </button>
              {tOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                  <div className="bg-white shadow-xl rounded-xl p-3 w-[520px] grid grid-cols-2 gap-1 border border-black/5">
                    {treatments.map((t) => (
                      <a key={t.title} href={t.href || "/#treatments"} className="px-3 py-2 rounded-lg text-[13px] text-[#0e2b2b] hover:bg-[#eef7f4] hover:text-[#14a68e] transition">{t.title}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavItem href="/#about">About Clinic</NavItem>
            <NavItem href="/#help">Help Me With</NavItem>
            <div className="hidden 2xl:block"><NavItem href="/#tourism">Dental Tourism</NavItem></div>
            <NavItem href="/#doctors">Meet Doctors</NavItem>
            <NavItem href="/blog">Blog</NavItem>
            <NavItem href="/#reviews">Reviews</NavItem>
            <NavItem href="/#contact">Contact</NavItem>
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2 2xl:gap-4 shrink-0">
            <div className="hidden 2xl:flex items-center gap-2 text-[12px] whitespace-nowrap">
              <span className="w-2 h-2 rounded-full bg-[#14a68e] animate-pulse" />
              <span className="text-[#0e2b2b]/70">Open Today</span>
            </div>
            <a href={`tel:${clinicInfo.phone}`} onClick={() => trackLead("phone_click", { button_location: "header_main" })} className="hidden xl:inline-flex items-center gap-2 whitespace-nowrap text-[12px] 2xl:text-[13px] font-semibold text-[#0e2b2b] border border-[#0e2b2b]/15 rounded-full px-3 2xl:px-4 py-2 hover:bg-[#0e2b2b]/5 transition"><Phone size={14} />{clinicInfo.phoneDisplay}</a>
            <a href={clinicInfo.whatsapp} target="_blank" rel="noreferrer" aria-label="Contact Dr. Khurana's Dental & Aesthetic Clinic on WhatsApp" onClick={() => trackLead("whatsapp_click", { button_location: "header_main" })} className="w-9 h-9 rounded-full border border-[#0e2b2b]/15 flex items-center justify-center hover:bg-[#0e2b2b]/5 transition"><MessageCircle size={16} aria-hidden="true" /></a>
            <div className="hidden 2xl:flex items-center gap-1 text-[12px] text-[#0e2b2b]/80 whitespace-nowrap"><Star size={12} className="fill-[#14a68e] text-[#14a68e]" />5-Star Rated</div>
            <a href="#contact" className="btn-primary whitespace-nowrap !px-4 !py-3 !text-[12px] 2xl:!px-[26px] 2xl:!py-[14px] 2xl:!text-[13px]">Book Appointment</a>
          </div>

          <button
              type="button"
              className="xl:hidden p-2"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={open}
              aria-controls="mobile-navigation"
            >
              {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div id="mobile-navigation" className="xl:hidden border-t border-black/5 bg-white px-6 py-4 flex flex-col gap-3">
            <a href="#treatments" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Treatments</a>
            <a href="#about" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">About Clinic</a>
            <a href="#help" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Help Me With</a>
            <a href="/#tourism" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Dental Tourism</a>
            <a href="/#doctors" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Meet Doctors</a>
            <a href="/blog" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Blog</a>
            <a href="/#reviews" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Reviews</a>
            <a href="/#contact" onClick={() => setOpen(false)} className="text-[13px] uppercase font-semibold">Contact</a>
            <a href="#contact" onClick={() => setOpen(false)} className="btn-primary justify-center mt-2">Book Appointment</a>
          </div>
        )}
      </div>
    </header>
  );
}
